import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getManagerTeamPerformance } from '../lib/api';
import MobileNav from '../components/MobileNav';
import { ArrowLeft, Clock, Trophy, Users, BarChart3, Zap, SprayCan } from 'lucide-react';

const PRESETS = [
  { key: '1w', label: '1 week', days: 7 },
  { key: '2w', label: '2 weeks', days: 14 },
  { key: '1m', label: 'Last month', days: 30 },
  { key: 'custom', label: 'Custom', days: null },
];

const fmt = (mins) => {
  if (mins === null || mins === undefined) return '--';
  const m = Math.round(mins);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
};

const iso = (d) => d.toISOString().split('T')[0];

const RANK_STYLES = {
  1: { ring: 'ring-2 ring-amber-300', badge: 'bg-amber-400 text-white', label: '🥇' },
  2: { ring: 'ring-1 ring-slate-300', badge: 'bg-slate-300 text-slate-700', label: '🥈' },
  3: { ring: 'ring-1 ring-orange-200', badge: 'bg-orange-300 text-white', label: '🥉' },
};

export default function ManagerPerformancePage() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('1w');
  const [customStart, setCustomStart] = useState(iso(new Date(Date.now() - 7 * 864e5)));
  const [customEnd, setCustomEnd] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [preset, customStart, customEnd]);

  const load = async () => {
    setLoading(true);
    try {
      let start, end;
      if (preset === 'custom') {
        start = customStart; end = customEnd;
      } else {
        const days = PRESETS.find((p) => p.key === preset).days;
        end = iso(new Date());
        start = iso(new Date(Date.now() - days * 864e5));
      }
      const res = await getManagerTeamPerformance(start, end);
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const ranked = data?.ranked || [];
  const noData = data?.no_data || [];
  const slowest = ranked.length ? Math.max(...ranked.map((r) => r.avg_minutes)) : 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
        <div className="max-w-4xl mx-auto px-4 pt-6 pb-8">
          <button onClick={() => navigate('/team')} className="flex items-center gap-1.5 text-white/90 hover:text-white text-sm font-medium mb-4" data-testid="manager-perf-back">
            <ArrowLeft size={18} /> Back to My Team
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
              <Trophy size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold leading-tight" data-testid="manager-perf-title">Cleaner Performance</h1>
              <p className="text-white/85 text-sm flex items-center gap-1.5">
                <SprayCan size={14} /> {data?.company_name || 'Your cleaning team'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-4">
        {/* Period selector */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="manager-perf-period">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`px-4 h-10 rounded-xl text-sm font-semibold border-2 transition-colors ${
                preset === p.key ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
              data-testid={`manager-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {preset === 'custom' && (
          <div className="flex flex-wrap items-end gap-3 mb-5 bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">From</label>
              <input type="date" value={customStart} max={customEnd} onChange={(e) => setCustomStart(e.target.value)} className="input-3d h-11 px-3" data-testid="manager-custom-start" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">To</label>
              <input type="date" value={customEnd} min={customStart} max={iso(new Date())} onChange={(e) => setCustomEnd(e.target.value)} className="input-3d h-11 px-3" data-testid="manager-custom-end" />
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-slate-400" data-testid="manager-perf-loading">Loading...</div>
        ) : !ranked.length ? (
          <div className="bg-white rounded-2xl p-10 shadow-sm border border-slate-100 text-center" data-testid="manager-perf-empty">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <BarChart3 size={30} className="text-slate-300" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">No cleaning data yet</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
              Once your cleaners start marking properties as ready, their average cleaning times will appear here as a leaderboard.
            </p>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="manager-stat-avg">
                <div className="flex items-center gap-2 text-slate-400 mb-2"><Clock size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Team average</span></div>
                <div className="text-3xl font-extrabold text-slate-900">{fmt(data.team_avg_minutes)}</div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="manager-stat-total">
                <div className="flex items-center gap-2 text-slate-400 mb-2"><Users size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Cleanings</span></div>
                <div className="text-3xl font-extrabold text-slate-900">{data.total_cleanings}</div>
                <p className="text-xs text-slate-400 mt-2">{ranked.length} cleaner{ranked.length !== 1 ? 's' : ''} ranked</p>
              </motion.div>
            </div>

            {/* Leaderboard */}
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Leaderboard · fastest first</h3>
            <div className="space-y-2.5" data-testid="manager-leaderboard">
              {ranked.map((r, i) => {
                const style = RANK_STYLES[r.rank];
                const pct = slowest ? Math.max(12, (r.avg_minutes / slowest) * 100) : 100;
                return (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className={`bg-white rounded-2xl p-4 shadow-sm border border-slate-100 ${style ? style.ring : ''}`}
                    data-testid={`manager-leaderboard-row-${r.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-extrabold flex-shrink-0 ${style ? style.badge : 'bg-slate-100 text-slate-500'}`}>
                        {style ? style.label : r.rank}
                      </div>
                      {r.photoUrl ? (
                        <img src={r.photoUrl} alt={r.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                          {r.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 truncate">{r.name}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="h-2 rounded-full bg-slate-100 flex-1 overflow-hidden max-w-[140px]">
                            <div className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] text-slate-400 flex-shrink-0">{r.cleanings} cleaning{r.cleanings !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg font-extrabold text-slate-900">{fmt(r.avg_minutes)}</div>
                        <div className="flex items-center justify-end gap-0.5 text-[11px] text-emerald-600 font-semibold">
                          <Zap size={11} /> best {fmt(r.fastest_minutes)}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {noData.length > 0 && (
              <>
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wide mb-2 mt-5 ml-1">No data in this period</h3>
                <div className="flex flex-wrap gap-2" data-testid="manager-no-data">
                  {noData.map((r) => (
                    <span key={r.id} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-sm text-purple-600">
                      <SprayCan size={11} />{r.name}
                    </span>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <MobileNav />
    </div>
  );
}
